import type { CustomFieldValues } from '@taki/data-model';
import type { TakiSdk } from '@taki/sdk';

/**
 * LE JEU DE DEMONSTRATION de l'app To do : 8 choses a faire d'une agence,
 * hors projet (les taches d'un projet vivent dans Projets, famille `tasks` ;
 * celles-ci dans `todos`, `docs/socle/todo-base.md`).
 *
 * Ce que chaque ligne sert : les quatre etapes ont une carte au moins, 2 sont
 * en retard, Celia (l'owner du seed, celle qui ouvre l'app) en porte 5 pour
 * remplir « Mes taches », Marc 2, et 1 n'a personne.
 *
 * Memes regles que `../projets/demo-dataset.ts` : tout passe par `@taki/sdk`,
 * IDEMPOTENT par `customFields.demoKey` (`td-*`), aucune suppression, aucune
 * date figee (tout se calcule depuis le lundi de la semaine courante), les
 * valeurs sont les valeurs STOCKEES du catalogue.
 */

const TYPE_TACHE = 'tache';
const CELIA = 'test@taki.lu';
const MARC = 'membre@taki.lu';

/** Ce que l'import a fait, rendu au hub Parametres. */
export interface DemoImportResult {
  created: number;
  skipped: number;
  message: string;
}

interface DemoTodo {
  demoKey: string;
  libelle: string;
  status: string;
  priority: string;
  assigne?: string;
  categorie: string;
  /** Echeance en jours depuis le lundi de la semaine courante ; absente = sans echeance. */
  echeance?: number;
}

export const DEMO_TODOS: ReadonlyArray<DemoTodo> = [
  { demoKey: 'td-1', libelle: 'Renouveler l’assurance des locaux', status: 'aFaire', priority: 'elevee', assigne: CELIA, categorie: 'administratif', echeance: -2 },
  { demoKey: 'td-2', libelle: 'Préparer l’entretien annuel de Marc', status: 'enCours', priority: 'moyenne', assigne: CELIA, categorie: 'equipe', echeance: 3 },
  { demoKey: 'td-3', libelle: 'Relancer les devis sans réponse', status: 'aFaire', priority: 'urgente', assigne: CELIA, categorie: 'commercial', echeance: -1 },
  { demoKey: 'td-4', libelle: 'Publier le portfolio de septembre', status: 'aFaire', priority: 'moyenne', assigne: CELIA, categorie: 'communication', echeance: 8 },
  { demoKey: 'td-5', libelle: 'Changer les licences du logiciel de mise en page', status: 'termine', priority: 'faible', assigne: CELIA, categorie: 'outils' },
  { demoKey: 'td-6', libelle: 'Commander les cartes de visite', status: 'enCours', priority: 'faible', assigne: MARC, categorie: 'administratif', echeance: 5 },
  { demoKey: 'td-7', libelle: 'Organiser le déjeuner d’équipe', status: 'aFaire', priority: 'moyenne', assigne: MARC, categorie: 'equipe', echeance: 10 },
  { demoKey: 'td-8', libelle: 'Archiver les dossiers 2025', status: 'annule', priority: 'faible', categorie: 'administratif' },
];

function lundiDeLaSemaine(reference: Date): Date {
  const lundi = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());
  lundi.setDate(lundi.getDate() - ((lundi.getDay() + 6) % 7));
  return lundi;
}

/** Midi local, le jour dit : une echeance est un jour, pas un rendez-vous. */
function midiLocal(lundi: Date, jours: number): Date {
  return new Date(lundi.getFullYear(), lundi.getMonth(), lundi.getDate() + jours, 12, 0, 0, 0);
}

/** Un sac sans clef vide : un champ sans valeur s'OMET. */
function sans<T extends Record<string, unknown>>(sac: T): CustomFieldValues {
  return Object.fromEntries(Object.entries(sac).filter(([, valeur]) => valeur !== undefined)) as CustomFieldValues;
}

/** Ecrire le jeu dans l'organisation courante, et ne creer que ce qui manque. */
export async function importDemoDataset(sdk: TakiSdk, orgId: string): Promise<DemoImportResult> {
  const lundi = lundiDeLaSemaine(new Date());
  const parAdresse = new Map(
    (await sdk.personnes.list({ archived: 'all' })).map((personne) => [personne.email.toLowerCase(), personne.personId]),
  );
  const connues = new Set(
    (await sdk.todos.list({ type: TYPE_TACHE }))
      .map((fiche) => fiche.customFields?.['demoKey'])
      .filter((clef): clef is string => typeof clef === 'string'),
  );

  let created = 0;
  let skipped = 0;
  for (const item of DEMO_TODOS) {
    if (connues.has(item.demoKey)) {
      skipped += 1;
      continue;
    }
    await sdk.todos.create({
      orgId,
      type: TYPE_TACHE,
      status: item.status,
      priority: item.priority,
      ...(item.echeance === undefined ? {} : { dueAt: midiLocal(lundi, item.echeance) }),
      customFields: sans({
        libelle: item.libelle,
        assigne: item.assigne === undefined ? undefined : parAdresse.get(item.assigne),
        categorie: item.categorie,
        demoKey: item.demoKey,
      }),
    });
    created += 1;
  }

  const lignes = (n: number): string => `${n} ${n > 1 ? 'lignes' : 'ligne'}`;
  const dejaLa = skipped === 0 ? "rien n'existait déjà" : `${skipped} déjà ${skipped > 1 ? 'présentes' : 'présente'}`;
  const message =
    created === 0
      ? `Le jeu de démonstration est déjà en place (${lignes(skipped)}).`
      : `${lignes(created)} ${created > 1 ? 'créées' : 'créée'}, ${dejaLa}.`;
  return { created, skipped, message };
}
